// Tab „Verknüpft“ (Handoff Sonstige Kontakte §2, Screen 06): Widget-Kacheln je Verknüpfung.
// Klick wie bisher: Dokument in neuem Tab, sonst Objekt; X hebt die Verknüpfung nach Rückfrage auf.
import { useState, type KeyboardEvent } from 'react';
import {
  AddressBook, ArrowUpRight, Buildings, CircleNotch, DoorOpen, EnvelopeSimple, FileText, Key, LinkSimple, Ticket, User, Wrench, X, type Icon,
} from '@phosphor-icons/react';
import { MeldungDialog } from '../../../../components/ui/MeldungDialog';
import { meldeKontaktGeaendert } from '../../../../lib/kontakte/detailTab';
import { useKontaktVerknuepfungen, ZIELTYP_LABEL, type VerknuepfungZeile } from '../../../../lib/kontakte/useKontaktVerknuepfungen';
import { FarbIcon, LeerKarte, TabLaedt } from './teile';

/** Icon und Palettenfarbe je Zieltyp (Prototyp LTYP, ergänzt um weitere Typen aus dem Code). */
const ZIEL: Record<string, { icon: Icon; farbe: number }> = {
  objekt: { icon: Buildings, farbe: 0 },
  einheit: { icon: DoorOpen, farbe: 5 },
  ticket: { icon: Ticket, farbe: 4 },
  dokument: { icon: FileText, farbe: 3 },
  mieter: { icon: User, farbe: 2 },
  eigentuemer: { icon: Key, farbe: 1 },
  dienstleister: { icon: Wrench, farbe: 2 },
  email: { icon: EnvelopeSimple, farbe: 0 },
  kontakt: { icon: AddressBook, farbe: 1 },
};

function tipVon(z: VerknuepfungZeile, klickbar: boolean): string {
  if (!klickbar) return `${z.titel} – ${ZIELTYP_LABEL[z.zielTyp] ?? z.zielTyp}`;
  return z.url ? `${z.titel} – Dokument in neuem Tab öffnen` : `${z.titel} – Zum Objekt`;
}

export function SonstigeVerknuepft({ id, onNavigateToObjekt }: { id: string; onNavigateToObjekt: (objektId: string) => void }) {
  const { zeilen, laedt, busyLinkId, loese } = useKontaktVerknuepfungen('kontakt', id);
  const [meldung, setMeldung] = useState<string | null>(null);

  const loeseEinzeln = async (z: VerknuepfungZeile) => {
    if (!z.linkId) return;
    if (!confirm(`Verknüpfung zu „${z.titel}" aufheben?`)) return;
    const fehler = await loese(z);
    if (fehler) setMeldung('Aufheben fehlgeschlagen: ' + fehler);
    else meldeKontaktGeaendert();
  };

  if (laedt) return <TabLaedt />;
  if (zeilen.length === 0) return <LeerKarte icon={LinkSimple} text="Keine Verknüpfungen" />;

  return (
    <>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 10 }}>
        {zeilen.map((z, i) => {
          const klickbar = !!(z.objektId || z.url);
          const beiKlick = () => {
            if (z.url) window.open(z.url, '_blank', 'noopener,noreferrer');
            else if (z.objektId) onNavigateToObjekt(z.objektId);
          };
          const taste = (e: KeyboardEvent<HTMLDivElement>) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); beiKlick(); }
          };
          const ziel = ZIEL[z.zielTyp] ?? { icon: LinkSimple, farbe: 5 };
          return (
            <div
              key={`${z.zielTyp}-${z.linkId ?? i}`}
              role={klickbar ? 'link' : undefined}
              tabIndex={klickbar ? 0 : undefined}
              onClick={klickbar ? beiKlick : undefined}
              onKeyDown={klickbar ? taste : undefined}
              data-tip={tipVon(z, klickbar)}
              className={`sk-glas sk-rise relative flex items-center ${klickbar ? 'sk-hebt3 cursor-pointer' : ''}`}
              style={{ isolation: 'isolate', overflow: 'hidden', borderRadius: 20, padding: 14, gap: 12, animationDuration: '.45s', animationDelay: `${Math.min(i, 12) * 35}ms` }}
            >
              <FarbIcon icon={ziel.icon} farbe={ziel.farbe} groesse={40} radius={13} iconGroesse={19} />
              <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
                <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{z.titel}</span>
                <span className="truncate" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: `var(--sk-av-${ziel.farbe}-b)` }}>{z.beziehung}</span>
                {z.extra && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{z.extra}</span>}
              </div>
              {klickbar && <ArrowUpRight aria-hidden weight="bold" size={14} color="var(--ink3)" className="shrink-0" />}
              {z.linkId && (
                <button
                  type="button"
                  aria-label={`Verknüpfung zu ${z.titel} aufheben`}
                  data-tip="Verknüpfung aufheben – Nur die Verbindung wird gelöst"
                  disabled={busyLinkId === z.linkId}
                  onClick={(e) => { e.stopPropagation(); void loeseEinzeln(z); }}
                  className="sk-loesen sk-tipp flex shrink-0 cursor-pointer items-center justify-center disabled:opacity-40"
                  style={{ width: 30, height: 30, border: 'none', borderRadius: 10 }}
                >
                  {busyLinkId === z.linkId ? <CircleNotch size={14} className="animate-spin" /> : <X weight="bold" size={13} />}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {meldung && <MeldungDialog fehler text={meldung} onSchliessen={() => setMeldung(null)} />}
    </>
  );
}
